
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { Typography, Avatar, Box, Divider, Card, CardContent, Button, CircularProgress } from "@mui/material";
import { CheckCircle, Cancel } from "@mui/icons-material";
import Navigation from "../../Reusbale/Navigation";
import Footer from "../../Reusbale/Footer";
import COLORS from "../../../../../constants/colors"; 
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import baseURL from "../../config";
import SnackbarAlert from "../../Reusbale/SnackbarAlert";
import { toast } from "react-toastify";

const UserDetailsPage = () => {
  const { id } = useParams(); // Get user ID from URL
  const [user, setUser] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true); // Set loading to true initially
  const [snackbar, setSnackbar] = useState({
  open: false,
  message: '',
  severity: 'error', // or 'success'
});

const navigate = useNavigate();



const handleCloseSnackbar = () => {
  setSnackbar(prev => ({ ...prev, open: false }));
};


  // Format date function
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true); // Set loading to true when fetching starts
        const response = await fetch(`${baseURL}/admin/user/${id}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        const data = await response.json();

        if (data.user) {
          setUser(data.user);
          setCampaigns(data.campaigns);
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    };

    fetchUserData();
  }, [id]);

const handleApproveUser = async (id) => {
  setLoading(true);
  try {
    const response = await fetch(`${baseURL}/admin/approve-user/${id}`, {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (response.ok) {
      toast.success("User approved successfully!");
      
    } else {
     

      toast.error(data.message || "Failed to approve user.");

    }
  } catch (error) {
    console.error("Approval error:", error);
    
      toast.error("Failed to approve user. Please try again.");
    
  }
  setLoading(false);
};


const handleDeleteAccount = async () => {
  if (window.confirm("Are you sure you want to delete this account? This action is irreversible!")) {
    try {
      const response = await fetch(`${baseURL}/admin/delete-account/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (response.ok) {
        

        toast.success('Account deleted successfully');

        setTimeout(() => {
          navigate(-1); // Go back to the previous page
        }, 1000);
      } else {
       

        toast.error(data.message || "Failed to delete account");

      }
    } catch (error) {
      

        toast.error(error.message || "An unexpected error occurred");

    }
  }
};


  if (loading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <CircularProgress size={18} /> {/* Adjust the size here */}
        <Typography sx={{ mt: 2, fontSize: "0.7rem", color: "text.secondary" }}>
          Loading user data... Please wait
        </Typography>
      </Box>
    );
  }
  

  if (!user) {
    return (
      <Typography variant="h6" color="error" sx={{ textAlign: "center", mt: 4, fontSize: '1rem' }}>
        User not found
      </Typography>
    );
  }

  const openCampaigns = campaigns.filter((c) => c.status === "open");
  const closedCampaigns = campaigns.filter((c) => c.status === "closed");

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#EEF2F7" }}>
      <Navigation />
      <Box
  component="main"
  sx={{
    flexGrow: 1,
    p: 2,
    minHeight: "100vh",
    marginLeft: { xs: 0, md: 35 },
    overflow: "hidden", // hides both horizontal and vertical overflows
  }}
>
        {/* User Details and Content */}
        <Typography
  variant="h6"
  sx={{
    color: "#666",
    mb: 2,
    fontWeight: "600",
    fontSize: "0.9rem",
    mt: { xs: 7, md: 0 }, // adds top margin on small screens only
  }}
>
          User Details
        </Typography>

        <Box
  sx={{
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "center", sm: "center" },
    gap: 3,
    backgroundColor: "#fff",
    borderRadius: "16px",
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
    border: "1px solid rgba(0,0,0,0.05)",
    p: 3,
    mb: 3,
    textAlign: { xs: "center", sm: "left" },
  }}
>
  <Avatar
    src={user.profileImage}
    alt={user.name}
    sx={{
      width: 90,
      height: 90,
      border: `1px solid ${COLORS.primary}`,
      mb: { xs: 2, sm: 0 }, // space below avatar on small screens
    }}
  />

  <Box sx={{ flexGrow: 1 }}>
    <Typography
      variant="h5"
      fontWeight="bold"
      color={COLORS.primary}
      sx={{ fontSize: "1rem" }}
    >
      {user.name}
    </Typography>
    <Typography
      variant="body2"
      color="textSecondary"
      sx={{ fontSize: "0.7rem" }}
    >
      {user.email}
    </Typography>
  </Box>

  {/* Approval Button */}
  {user.isVerified === "no" ? (
    <Button
      variant="contained"
      color="success"
      size="small"
      sx={{
        textTransform: 'none',
        minWidth: '120px',
        mt: { xs: 2, sm: 0 }, // add top margin for spacing on mobile
      }}
      startIcon={!loading && <CheckCircleIcon />}
      onClick={() => handleApproveUser(id)}
      disabled={loading}
    >
      {loading ? (
        <>
          <CircularProgress size={18} color="inherit" sx={{ marginRight: 1 }} />
          Please wait...
        </>
      ) : (
        "Approve"
      )}
    </Button>
  ) : (
    <Button
      variant="outlined"
      color="success"
      sx={{
        textTransform: 'none',
        mt: { xs: 2, sm: 0 },
      }}
      size="small"
      startIcon={<CheckCircleIcon />}
      disabled
    >
      Approved
    </Button>
  )}
</Box>

        {/* Account and Financial Summary */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3, mb: 3 }}>
          <Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>Account Information</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: "0.7rem" }}><strong>Phone:</strong> {user.phone}</Typography>
            <Typography sx={{ fontSize: "0.7rem" }}><strong>Verified:</strong> {user.isVerified === "yes" ? "Yes" : "No"}</Typography>
            <Typography sx={{ fontSize: "0.7rem" }}><strong>Joined:</strong> {formatDate(user.created_at)}</Typography>
          </Box>

          <Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, flex: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>Financial Summary</Typography>
            <Divider sx={{ mb: 2 }} />
            <Typography sx={{ fontSize: "0.7rem" }}><strong>Total Raised:</strong> UGX {user.total_raised_campaigns ? user.total_raised_campaigns.toLocaleString() : "0"}</Typography>
            <Typography sx={{ fontSize: "0.7rem" }}><strong>Total Withdrawn:</strong> UGX {user.total_widthdrawn ? user.total_widthdrawn.toLocaleString() : "0"}</Typography>
            <Typography sx={{ fontSize: "0.7rem" }}><strong>Balance:</strong> UGX {user.balance ? user.balance.toLocaleString() : "0"}</Typography>
          </Box>
        </Box>

        {/* Campaigns and Account Actions */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
           {/* Campaigns Section */}
          
          {/* Open Campaigns */}
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight="bold" sx={{ fontSize: "0.8rem", mb: 2 }}>Open Campaigns</Typography>
            {openCampaigns.length > 0 ? (
              openCampaigns.map((c) => (
                <Card key={c.id} sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid rgba(0,0,0,0.05)" }}>
                  <CardContent>
                    {/* Campaign Image */}
                    {c.image_url && (
                      <Box 
                        component="img" 
                        src={c.image_url} 
                        alt={c.name} 
                        sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 3, mb: 1 }} 
                      />
                    )}
                    <Typography fontWeight="bold" sx={{ fontSize: "0.8rem", mb: 1 }}>
                      {c.name} <CheckCircle color="success" />
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", mb: 0.5 }}>
                      <strong>Raised:</strong> UGX {c.raised.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", mb: 0.5 }}>
                      <strong>Goal:</strong> UGX {c.goal.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "gray" }}>
                      <strong>Created:</strong> {formatDate(c.created_at)}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography sx={{ fontSize: "0.7rem" }}>No open campaigns.</Typography>
            )}
          </Box>

          {/* Closed Campaigns */}
          <Box sx={{ flex: 1 }}>
            <Typography fontWeight="bold" sx={{ fontSize: "0.8rem", mb: 2 }}>Closed Campaigns</Typography>
            {closedCampaigns.length > 0 ? (
              closedCampaigns.map((c) => (
                <Card key={c.id} sx={{ mb: 2, backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid rgba(0,0,0,0.05)" }}>
                  <CardContent>
                    {/* Campaign Image */}
                    {c.image_url && (
                      <Box 
                        component="img" 
                        src={c.image_url} 
                        alt={c.name} 
                        sx={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 3, mb: 1 }} 
                      />
                    )}
                    <Typography fontWeight="bold" sx={{ fontSize: "0.8rem", mb: 1 }}>
                      {c.name} <Cancel color="error" />
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", mb: 0.5 }}>
                      <strong>Raised:</strong> UGX {c.raised.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", mb: 0.5 }}>
                      <strong>Goal:</strong> UGX {c.goal.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "gray" }}>
                      <strong>Created:</strong> {formatDate(c.created_at)}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Typography sx={{ fontSize: "0.7rem" }}>No closed campaigns.</Typography>
            )}
          </Box>
        </Box>

                {/* ID Photos Section */}
<Box sx={{ backgroundColor: "#fff", borderRadius: "16px", boxShadow: "0 2px 16px rgba(0,0,0,0.06)", border: "1px solid rgba(0,0,0,0.05)", p: 3, mb: 3, mt: 2 }}>
  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "0.95rem", letterSpacing: "-0.2px", mb: 1 }}>National ID Photos</Typography>
  <Divider sx={{ mb: 2 }} />
  <Box sx={{ display: "flex", gap: 3, justifyContent: "space-between" }}>
    {/* Display Front ID Image */}
    {user.idFront ? (
      <Box sx={{ textAlign: "center", flex: 1 }}>
        <img
          src={`${user.idFront}`}
          alt="ID Front"
          style={{ width: "100%", height: "200px", objectFit: "cover", borderRadius: "8px" }}
        />
        <Typography sx={{ fontSize: "0.7rem", mt: 1 }}>Front ID</Typography>
      </Box>
    ) : (
      <Typography sx={{ fontSize: "0.7rem", color: "gray", flex: 1 }}>No front ID photo provided.</Typography>
    )}

    {/* Display Back ID Image */}
    {user.idBack ? (
      <Box sx={{ textAlign: "center", flex: 1 }}>
        <img
          src={`${user.idBack}`}
          alt="ID Back"
          style={{ width: "100%", height: "200px", objectFit: "cover", borderRadius: "8px" }}
        />
        <Typography sx={{ fontSize: "0.7rem", mt: 1 }}>Back ID</Typography>
      </Box>
    ) : (
      <Typography sx={{ fontSize: "0.7rem", color: "gray", flex: 1 }}>No back ID photo provided.</Typography>
    )}
  </Box>
</Box>


 {/* Delete Account Section */}
 <Box sx={{ backgroundColor: "#fff", borderRadius: 3, p: 3, mb: 3, mt: 2, textAlign: "left" }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ fontSize: "0.9rem", mb: 1 }}>
            Account Actions
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography sx={{ fontSize: "0.8rem", color: "gray", mb: 2 }}>
            Deleting this account will permanently remove the user and all their associated data, including fundraising campaigns and financial records. This action cannot be undone.
          </Typography>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            sx={{
              textTransform: "none",
            }}
          >
            Delete Account
          </Button>
        </Box>

        {/* Account Deletion Section (existing logic) */}
        <Footer />



      </Box>
      <SnackbarAlert
  open={snackbar.open}
  message={snackbar.message}
  severity={snackbar.severity}
  handleClose={handleCloseSnackbar}
/>

    </Box>
  );
};

export default UserDetailsPage;
